import React, { useEffect, useState, useMemo } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { localizeError } from "@/lib/errors";
import { Plus, Trash2, Pencil, User, Phone, Mail, CreditCard, Calendar, MapPin, Building2, Search, UserCheck, BadgeCheck, AlertTriangle, Clock } from "lucide-react";
import { WeeklyRotaEditor } from "@/pages/StaffRota";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { canViewEmployeeHr } from "@/lib/roles";

const emptyEmp = {
  full_name: "", full_name_en: "", national_id: "", id_type: "national", id_expiry: "", date_of_birth: "", gender: "", nationality: "",
  phone: "", email: "", address: "", emergency_contact_name: "", emergency_contact_phone: "",
  department_id: "", job_title: "", hire_date: "", contract_type: "full_time", branch_id: "",
  basic_salary: 0, housing_allowance: 0, transport_allowance: 0, other_allowance: 0,
  monthly_sales_target: 0,
  bank_name: "", iban: "", photo_url: "", notes: "", active: true,
};

const ID_TYPES = [
  { id: "national", labelEn: "National ID", labelAr: "هوية وطنية" },
  { id: "iqama", labelEn: "Iqama", labelAr: "إقامة" },
  { id: "passport", labelEn: "Passport", labelAr: "جواز سفر" },
];

const CONTRACT_TYPES = [
  { id: "full_time", labelEn: "Full Time", labelAr: "دوام كامل" },
  { id: "part_time", labelEn: "Part Time", labelAr: "دوام جزئي" },
  { id: "contract", labelEn: "Contract", labelAr: "عقد" },
];

const TABS = [
  { id: "personal", labelEn: "Personal Info", labelAr: "البيانات الشخصية" },
  { id: "contact", labelEn: "Contact", labelAr: "التواصل" },
  { id: "job", labelEn: "Job", labelAr: "بيانات الوظيفة" },
  { id: "salary", labelEn: "Salary", labelAr: "الراتب" },
  { id: "schedule", labelEn: "Schedule", labelAr: "الجدول" },
];

const inputCls = "w-full px-3 py-2 rounded-md bg-[var(--bg)] border border-[var(--border)] text-[var(--text)] focus:border-[var(--gold)] outline-none";
const labelCls = "text-xs text-[var(--text-muted)] mb-1 block";

export default function Employees() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const canHr = canViewEmployeeHr(user?.role);
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { activeBranchId, activeBranch, branches: allBranches } = useBranch();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp);
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState("personal");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  // Branch scope: "" (auto = active branch) | branch_id | "all"
  const [branchScope, setBranchScope] = useState("");

  const load = async () => {
    const params = {};
    if (branchScope === "all") params.branch = "all";
    else if (branchScope) params.branch = branchScope;
    const requests = [
      api.get("/employees", { params }),
      api.get("/departments"),
      api.get("/branches"),
    ];
    if (canHr) requests.push(api.get("/users"));
    const [e, d, b, u] = await Promise.all(requests);
    setItems(e.data); setDepartments(d.data); setBranches(b.data);
    setUsers(canHr ? (u?.data || []) : []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchScope, activeBranchId, canHr]);

  const save = async () => {
    if (!form.full_name || !form.national_id) return toast.error(lang === "ar" ? "الاسم ورقم الهوية مطلوبان" : "Name and ID required");
    if (!form.branch_id) {
      setTab("job");
      return toast.error(lang === "ar" ? "اختيار الفرع مطلوب — يظهر الموظف في الفرع المحدد فقط" : "Branch is required — employee appears in the chosen branch only");
    }
    try {
      // Schedule is saved separately via WeeklyRotaEditor — don't send weekly_rota here.
      const { weekly_rota, weekly_rota_updated_at, employee_id, employee_code, user_id, created_at, ...payload } = form;
      if (!editId && !payload.branch_id && activeBranchId) payload.branch_id = activeBranchId;
      if (editId) await api.put(`/employees/${editId}`, payload);
      else await api.post("/employees", payload);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm(emptyEmp); setEditId(null); setTab("personal"); load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الموظف؟" : "Delete employee?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const edit = (emp) => {
    setForm({ ...emptyEmp, ...emp });
    setEditId(emp.employee_id);
    setTab("personal");
    setOpen(true);
  };

  const deptMap = useMemo(() => departments.reduce((m, d) => { m[d.department_id] = d; return m; }, {}), [departments]);
  const userMap = useMemo(() => users.reduce((m, u) => { m[u.user_id] = u; return m; }, {}), [users]);
  const branchMap = useMemo(() => branches.reduce((m, b) => { m[b.branch_id] = b; return m; }, {}), [branches]);

  const visibleTabs = useMemo(
    () => (canHr ? TABS : TABS.filter((tt) => tt.id === "job")),
    [canHr],
  );

  const filtered = useMemo(() => {
    return items.filter((e) => {
      if (deptFilter && e.department_id !== deptFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        const idMatch = canHr && e.national_id?.includes(s);
        if (!e.full_name?.toLowerCase().includes(s) && !idMatch && !e.phone?.includes(s) && !e.employee_code?.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [items, deptFilter, search, canHr]);

  const totalSalary = (e) =>
    (parseFloat(e.basic_salary) || 0) +
    (parseFloat(e.housing_allowance) || 0) +
    (parseFloat(e.transport_allowance) || 0) +
    (parseFloat(e.other_allowance) || 0);

  const isIdExpiringSoon = (d) => {
    if (!d) return false;
    const diff = (new Date(d) - new Date()) / (1000 * 60 * 60 * 24);
    return diff > 0 && diff <= 60;
  };
  const isIdExpired = (d) => {
    if (!d) return false;
    return new Date(d) < new Date();
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "الموظفون" : "Employees"}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{items.length} {lang === "ar" ? "موظف" : "employees"} · {items.filter((e) => e.active).length} {lang === "ar" ? "نشط" : "active"}</p>
        </div>
        {canHr && (
          <button data-testid="add-employee-btn" onClick={() => { setForm({ ...emptyEmp, branch_id: activeBranchId || "" }); setEditId(null); setTab("personal"); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--gold)] text-white">
            <Plus size={16} /> {lang === "ar" ? "موظف جديد" : "New Employee"}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={14} className="absolute top-1/2 start-3 -translate-y-1/2 text-[var(--text-muted)]" />
          <input placeholder={lang === "ar" ? (canHr ? "بحث بالاسم، الهوية، الرقم..." : "بحث بالاسم، الرقم...") : (canHr ? "Search by name, ID, phone..." : "Search by name, phone...")} className={`${inputCls} ps-9`} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="emp-search" />
        </div>
        <select className={inputCls + " md:w-auto min-w-[200px]"} value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="">{lang === "ar" ? "كل الأقسام" : "All departments"}</option>
          {departments.map((d) => <option key={d.department_id} value={d.department_id}>{lang === "ar" ? d.name_ar : d.name_en}</option>)}
        </select>
        <select className={inputCls + " md:w-auto min-w-[200px]"} value={branchScope} onChange={(e) => setBranchScope(e.target.value)} data-testid="emp-branch-filter">
          <option value="">{lang === "ar" ? `الفرع النشط (${activeBranch ? (lang === "ar" ? activeBranch.name_ar : activeBranch.name_en) : "—"})` : `Active branch (${activeBranch ? (lang === "ar" ? activeBranch.name_ar : activeBranch.name_en) : "—"})`}</option>
          {allBranches.map((b) => <option key={b.branch_id} value={b.branch_id}>{lang === "ar" ? b.name_ar : b.name_en}</option>)}
          <option value="all">{lang === "ar" ? "جميع الفروع" : "All branches"}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] border-b border-[var(--border)]">
            <tr>
              <th className="p-3 text-start">{lang === "ar" ? "الكود" : "Code"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الاسم" : "Name"}</th>
              {canHr && <th className="p-3 text-start">{lang === "ar" ? "الهوية" : "ID"}</th>}
              <th className="p-3 text-start">{lang === "ar" ? "القسم" : "Department"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الفرع" : "Branch"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الوظيفة" : "Job Title"}</th>
              <th className="p-3 text-start">{lang === "ar" ? "الهاتف" : "Phone"}</th>
              {canHr && <th className="p-3 text-end">{lang === "ar" ? "الراتب" : "Salary"}</th>}
              {canHr && <th className="p-3 text-center">{lang === "ar" ? "حساب" : "Account"}</th>}
              <th className="p-3 text-start">{lang === "ar" ? "الحالة" : "Status"}</th>
              {canHr && <th className="p-3 text-center">{lang === "ar" ? "الإجراءات" : "Actions"}</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const dept = deptMap[e.department_id];
              const hasUser = !!e.user_id && !!userMap[e.user_id];
              return (
                <tr key={e.employee_id} className={`border-t border-[var(--border)] hover:bg-[var(--bg)]/50 ${canHr ? "cursor-pointer" : ""}`} onClick={canHr ? () => edit(e) : undefined}>
                  <td className="p-3 font-mono text-[var(--gold)] font-semibold">{e.employee_code}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {e.photo_url ? <img src={e.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-[var(--gold)]/10 flex items-center justify-center text-[var(--gold)] text-xs font-bold">{(e.full_name || "?").charAt(0).toUpperCase()}</div>}
                      <div>
                        <div className="font-medium">{e.full_name}</div>
                        {e.full_name_en && <div className="text-xs text-[var(--text-muted)]">{e.full_name_en}</div>}
                      </div>
                    </div>
                  </td>
                  {canHr && (
                    <td className="p-3">
                      <div className="text-xs font-mono">{e.national_id}</div>
                      {e.id_expiry && (
                        <div className={`text-[10px] mt-0.5 ${isIdExpired(e.id_expiry) ? "text-rose-600 font-semibold" : isIdExpiringSoon(e.id_expiry) ? "text-amber-600" : "text-[var(--text-muted)]"}`}>
                          {isIdExpired(e.id_expiry) && <AlertTriangle size={10} className="inline" />} {lang === "ar" ? "ينتهي:" : "Exp:"} {e.id_expiry}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="p-3">
                    {dept ? <span className="text-xs px-2 py-1 rounded" style={{ background: `${dept.color}20`, color: dept.color }}>{lang === "ar" ? dept.name_ar : dept.name_en}</span> : <span className="text-[var(--text-muted)]">—</span>}
                  </td>
                  <td className="p-3">
                    {e.branch_id && branchMap[e.branch_id]
                      ? <span className="text-xs px-2 py-1 rounded inline-flex items-center gap-1 bg-[var(--gold)]/10 text-[var(--gold)]"><Building2 size={10}/>{lang === "ar" ? (branchMap[e.branch_id].name_ar || branchMap[e.branch_id].name_en) : (branchMap[e.branch_id].name_en || branchMap[e.branch_id].name_ar)}</span>
                      : <span className="text-xs px-2 py-1 rounded inline-flex items-center gap-1 bg-rose-50 text-rose-600" title={lang === "ar" ? "غير محدد — يلزم تعديل الموظف" : "Unassigned — please edit"}><AlertTriangle size={10}/>{lang === "ar" ? "غير محدد" : "Unassigned"}</span>}
                  </td>
                  <td className="p-3 text-[var(--text-muted)]">{e.job_title || "—"}</td>
                  <td className="p-3 font-mono text-xs" dir="ltr">{e.phone || "—"}</td>
                  {canHr && <td className="p-3 text-end gold-text font-semibold">{totalSalary(e).toFixed(0)}</td>}
                  {canHr && <td className="p-3 text-center">{hasUser ? <BadgeCheck size={16} className="inline text-green-600" /> : <span className="text-[var(--text-muted)]">—</span>}</td>}
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${e.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{e.active ? (lang === "ar" ? "نشط" : "Active") : (lang === "ar" ? "غير نشط" : "Inactive")}</span></td>
                  {canHr && (
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <button onClick={(ev) => { ev.stopPropagation(); edit(e); }} className="p-1.5 rounded hover:bg-[var(--gold)]/10 text-[var(--gold)]"><Pencil size={14} /></button>
                        <button data-testid={`delete-emp-${e.employee_id}`} onClick={(ev) => { ev.stopPropagation(); remove(e.employee_id); }} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={canHr ? 11 : 7} className="text-center text-[var(--text-muted)] py-12">{lang === "ar" ? "لا يوجد موظفون" : "No employees"}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {open && canHr && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="bg-[var(--surface)] rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col" onMouseDown={onModalPanelMouseDown}>
            <div className="p-5 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-heading gold-text">{editId ? (lang === "ar" ? "تعديل موظف" : "Edit Employee") : (lang === "ar" ? "موظف جديد" : "New Employee")}</h3>
                {editId && <p className="text-xs text-[var(--text-muted)] font-mono mt-1">{form.employee_code}</p>}
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {lang === "ar" ? "نشط" : "Active"}</label>
            </div>

            {/* Tabs */}
            <div className="px-5 pt-3 border-b border-[var(--border)] flex gap-1 overflow-x-auto">
              {visibleTabs.map((tt) => (
                <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`emp-tab-${tt.id}`} className={`px-4 py-2 text-sm rounded-t-md whitespace-nowrap ${tab === tt.id ? "bg-[var(--bg)] text-[var(--gold)] border-b-2 border-[var(--gold)]" : "text-[var(--text-muted)] hover:text-[var(--text)]"}`}>
                  {lang === "ar" ? tt.labelAr : tt.labelEn}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-auto p-5">
              {tab === "personal" && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><label className={labelCls}>{lang === "ar" ? "الاسم الكامل (عربي)" : "Full Name (AR) *"}</label><input data-testid="emp-full-name" className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "الاسم بالإنجليزية" : "Full Name (EN)"}</label><input className={inputCls} value={form.full_name_en} onChange={(e) => setForm({ ...form, full_name_en: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "نوع الهوية" : "ID Type"}</label>
                    <select className={inputCls} value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })}>
                      {ID_TYPES.map((t) => <option key={t.id} value={t.id}>{lang === "ar" ? t.labelAr : t.labelEn}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>{lang === "ar" ? "رقم الهوية *" : "ID Number *"}</label><input data-testid="emp-national-id" className={inputCls} value={form.national_id} onChange={(e) => setForm({ ...form, national_id: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "تاريخ انتهاء الهوية" : "ID Expiry Date"}</label><input type="date" className={inputCls} value={form.id_expiry || ""} onChange={(e) => setForm({ ...form, id_expiry: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "تاريخ الميلاد" : "Date of Birth"}</label><input type="date" className={inputCls} value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "الجنس" : "Gender"}</label>
                    <select className={inputCls} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
                      <option value="">—</option>
                      <option value="male">{lang === "ar" ? "ذكر" : "Male"}</option>
                      <option value="female">{lang === "ar" ? "أنثى" : "Female"}</option>
                    </select>
                  </div>
                  <div><label className={labelCls}>{lang === "ar" ? "الجنسية" : "Nationality"}</label><input className={inputCls} value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></div>
                  <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "رابط الصورة" : "Photo URL"}</label><input className={inputCls} placeholder="https://..." value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></div>
                </div>
              )}

              {tab === "contact" && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><label className={labelCls}>{lang === "ar" ? "رقم الهاتف" : "Phone"}</label><input className={inputCls} dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "البريد الإلكتروني" : "Email"}</label><input className={inputCls} dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "العنوان" : "Address"}</label><textarea rows={2} className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "اسم جهة اتصال الطوارئ" : "Emergency Contact Name"}</label><input className={inputCls} value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "هاتف الطوارئ" : "Emergency Phone"}</label><input className={inputCls} dir="ltr" value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>
                </div>
              )}

              {tab === "job" && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div><label className={labelCls}>{lang === "ar" ? "القسم" : "Department"}</label>
                    <select data-testid="emp-department" className={inputCls} value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                      <option value="">—</option>
                      {departments.map((d) => <option key={d.department_id} value={d.department_id}>{lang === "ar" ? d.name_ar : d.name_en}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>{lang === "ar" ? "المسمى الوظيفي" : "Job Title"}</label><input className={inputCls} value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "تاريخ التعيين" : "Hire Date"}</label><input type="date" className={inputCls} value={form.hire_date || ""} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></div>
                  <div><label className={labelCls}>{lang === "ar" ? "نوع العقد" : "Contract Type"}</label>
                    <select className={inputCls} value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })}>
                      {CONTRACT_TYPES.map((t) => <option key={t.id} value={t.id}>{lang === "ar" ? t.labelAr : t.labelEn}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{lang === "ar" ? "الفرع *" : "Branch *"}</label>
                    <select
                      data-testid="emp-branch"
                      required
                      className={`${inputCls} ${!form.branch_id ? "border-rose-400" : ""}`}
                      value={form.branch_id || ""}
                      onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                    >
                      <option value="">{lang === "ar" ? "— اختر الفرع —" : "— Select branch —"}</option>
                      {branches.map((b) => <option key={b.branch_id} value={b.branch_id}>{lang === "ar" ? b.name_ar : b.name_en}</option>)}
                    </select>
                    {!form.branch_id && (
                      <div className="text-[10px] text-rose-500 mt-1 flex items-center gap-1">
                        <AlertTriangle size={10}/>
                        {lang === "ar" ? "الفرع إلزامي — يحدد ظهور الموظف في الفروع وتقاريرها" : "Branch is required — determines where this employee appears"}
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2"><label className={labelCls}>{lang === "ar" ? "ملاحظات" : "Notes"}</label><textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
              )}

              {tab === "salary" && (
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><label className={labelCls}>{lang === "ar" ? "الراتب الأساسي" : "Basic Salary"}</label><input type="number" data-testid="emp-basic-salary" className={inputCls} value={form.basic_salary} onChange={(e) => setForm({ ...form, basic_salary: parseFloat(e.target.value || 0) })} /></div>
                    <div><label className={labelCls}>{lang === "ar" ? "بدل السكن" : "Housing Allowance"}</label><input type="number" className={inputCls} value={form.housing_allowance} onChange={(e) => setForm({ ...form, housing_allowance: parseFloat(e.target.value || 0) })} /></div>
                    <div><label className={labelCls}>{lang === "ar" ? "بدل المواصلات" : "Transport Allowance"}</label><input type="number" className={inputCls} value={form.transport_allowance} onChange={(e) => setForm({ ...form, transport_allowance: parseFloat(e.target.value || 0) })} /></div>
                    <div><label className={labelCls}>{lang === "ar" ? "بدلات أخرى" : "Other Allowance"}</label><input type="number" className={inputCls} value={form.other_allowance} onChange={(e) => setForm({ ...form, other_allowance: parseFloat(e.target.value || 0) })} /></div>
                    <div><label className={labelCls}>{lang === "ar" ? "هدف المبيعات الشهري (ر.ق)" : "Monthly Sales Target (QAR)"}</label><input type="number" min="0" step="100" data-testid="emp-monthly-sales-target" className={inputCls} value={form.monthly_sales_target || 0} onChange={(e) => setForm({ ...form, monthly_sales_target: parseFloat(e.target.value || 0) })} /></div>
                    <div className="md:col-span-2 bg-[var(--bg)] p-3 rounded-md flex items-center justify-between">
                      <span className="font-semibold">{lang === "ar" ? "إجمالي الراتب الشهري" : "Total Monthly Salary"}</span>
                      <span className="text-2xl gold-text font-bold">{totalSalary(form).toFixed(2)} {lang === "ar" ? "ر.ق" : "QAR"}</span>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-[var(--border)]">
                    <div><label className={labelCls}>{lang === "ar" ? "اسم البنك" : "Bank Name"}</label><input className={inputCls} value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} /></div>
                    <div><label className={labelCls}>IBAN</label><input className={inputCls} dir="ltr" value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
                  </div>
                </div>
              )}

              {tab === "schedule" && (
                <div className="space-y-3">
                  {!editId ? (
                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3">
                      {lang === "ar" ? "احفظ الموظف أولاً ثم عدّل جدول الدوام." : "Save the employee first, then configure their schedule."}
                    </p>
                  ) : (
                    <WeeklyRotaEditor employeeId={editId} lang={lang} branchId={form.branch_id} />
                  )}
                </div>
              )}
            </div>

            <div className="p-5 border-t border-[var(--border)] flex gap-2 justify-end">
              <button onClick={() => setOpen(false)} className="px-5 py-2 border border-[var(--border)] rounded-md">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button data-testid="save-employee-btn" onClick={save} className="px-6 py-2 bg-[var(--gold)] text-white rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
